import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Datasource as DatasourceCtor } from 'ngx-ui-scroll';
import { of } from 'rxjs';
import { take, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IAppleReenrollmentsSummary } from '../../interfaces/apple-reenrollments-summary'
import { IAppleReenrollmentDetails } from '../../interfaces/apple-reenrollment-details';
import { IAppleReenrollmentsDetailsChunkResponse } from '../../interfaces/apple-reenrollments-details-chunk';
import { ClientStatsSectionBase } from '../../../client-workspace/components/client-stats-section-base';
import { AppleReenrollmentsService } from '../../services/apple-reenrollments.service';
import { PortalUserService } from 'src/app/services/admin/portal-user.service';

@Component({
  selector: 'app-apple-reenrollments',
  standalone: false,
  templateUrl: './apple-reenrollments.component.html',
  styleUrl: './apple-reenrollments.component.css'
})
export class AppleReenrollmentsComponent
  extends ClientStatsSectionBase<IAppleReenrollmentDetails>
  implements OnInit, OnDestroy {

  private readonly appleReenrollmentsService = inject(AppleReenrollmentsService);
  private readonly portalUserService = inject(PortalUserService);

  // ── Apple Re-enrollments-specific state ───────────────────────────────────
  isSamsungPlatformEnabled = false;

  totalReenrollmentAttemptCnt = 0;
  totalPassRenderedCnt = 0;
  totalRegisteredCnt = 0;

  brandCodes: any[] = [];  

  viewingModel: 'Summary' | 'Details' = 'Summary';

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override ngOnInit(): void {

    this.sortField       = 'eventId';
    this.pdfSectionTitle = 'Apple Re-enrollments';
    this.selectedChartType = 'bar';
    this.chartOptions = this.getOptionsForType(this.selectedChartType);

    this.selectedDateFrom = this.sharedService.getPreviousDate(7);
    this.selectedDateTo = this.sharedService.getPreviousDate(1); 

    this.summaryColumns = [
      { field: 'brandCode',           header: 'Brand Code',             width: '15%', display: 'table-cell' },
      { field: 'brandName',           header: 'Brand Name',             width: '25%', display: 'table-cell' },
      { field: 'enrollmentRequestedCnt', header: 'Enrollment Requests', width: '20%', display: 'table-cell' },
      { field: 'passRenderedCnt',      header: 'Pass Rendered',       width: '20%', display: 'table-cell' },
      { field: 'registeredCnt',        header: 'Pass Registered',     width: '20%', display: 'table-cell' },
    ];

    this.getGrantedBrands();
    this.initDatasource();
  }

  onSetViewingModel(model: 'Summary' | 'Details') {
    this.viewingModel = model;  
  }

  // Find out the list of granted brands to the current user, so we can populate the dropdown for brands
  getGrantedBrands() {
    this.brandCodes = [];
    this.isSpinnerHidden = false;
    this.brandCodes.push({label: '', value: null});
    this.portalUserService.getBillerBrands()
    .subscribe({
      next: (response) => {
          console.log('getGrantedBrands - response', response);
        if (Array.isArray(response)) {
          response.forEach(brand => {
            if (brand.isActive) {
              this.brandCodes.push({label: brand.brandCode + ' - ' + brand.brandName, value: brand.brandCode});
            }
          })
        }
        this.isSpinnerHidden = true;
      },
      error: (error) => {
        this.isSpinnerHidden = true;      },
      complete: () => {
      }
    });
  }  


  override ngOnDestroy(): void {}

  // ── Params override — also reads Samsung flag ─────────────────────────────

  protected override onParamsChanged(key: string, params?: Record<string, any>): void {
    super.onParamsChanged(key, params);
  }

  // ── Abstract implementations ──────────────────────────────────────────────

  override isSelected(row: IAppleReenrollmentDetails): boolean {
    return this.selectedRow?.eventId === row.eventId;
  }

  // When the user clicks on the Search button of the selection criteria.
  // Both the Summary and the Details views must refetch from the backend so the
  // user always sees data consistent with the current criteria, regardless of
  // which view is currently active.
  onSearchClick(event: any) {
    // 1. Refresh the Summary view's data.
    this.fetchData();

    // 2. Reload the Details virtual-scroll datasource. The previous dataset's
    //    minId/maxId are stale for the new criteria, so a full pagination reset
    //    is required; the sort is also returned to the default field/direction
    //    because cursor seeding for desc relies on a valid maxId that we don't
    //    yet have for the new dataset.
    this.sortField = 'eventId';
    this.sortDirection = 'asc';
    this.pagination.reset();
    this.pagination.seedForSort(this.sortDirection);
    this.datasource?.adapter.reload(0);
  }

  onAggregationTypeChanged(_data: any): void {
    this.fetchData();
  }

  override fetchData(): void {
    this.customLoggerService.logDebug('Fetching Apple re-enrollments summary - Brand:', 
      this.selectedBrandCode,
      'Date From:', this.selectedDateFrom, 'Date To:', this.selectedDateTo);

    // if (!(this.selectedBrandCode && this.selectedDateFrom && this.selectedDateTo)) {
    //   this.showMissingCriteriaWarning(
    //     'Make sure to select a brand and the date interval in the selection criteria to retrieve database records.'
    //   );
    //   return;
    // }

    this.isSpinnerHidden           = false;
    this.hasData                   = false;
    this.totalReenrollmentAttemptCnt = 0;
    this.totalPassRenderedCnt      = 0;
    this.totalRegisteredCnt        = 0;

    this.appleReenrollmentsService
      .getAppleReenrollmentsSummary(this.selectedBrandCode, this.selectedDateFrom, this.selectedDateTo)
      .subscribe({
        next: (result: IAppleReenrollmentsSummary[]) => {
          this.customLoggerService.logDebug('fetchData - result:', result);
          this.isSpinnerHidden = true;

          if (result.length === 0) {
            this.summaryRows = [];
            this.chartData   = { labels: [], datasets: [] };
            this.showNoDataWarning();
            return;
          }

          result.forEach(r => {
            this.totalReenrollmentAttemptCnt += Number(r.enrollmentRequestedCnt);
            this.totalPassRenderedCnt      += Number(r.passRenderedCnt);
            this.totalRegisteredCnt        += Number(r.registeredCnt);
          });
          this.summaryRows = result;

          const labels: string[]                 = [];
          const reenrollmentAttemptsData: number[] = [];
          const passRenderedData: number[]       = [];
          const registeredData: number[]         = [];

          result.forEach(r => {
            const label = r.brandCode;
            if (!labels.includes(label)) { labels.push(label); }
          });

          labels.forEach((brandCode, index) => {
            const match = result.find(r => r.brandCode === brandCode );
            reenrollmentAttemptsData[index] = match ? Number(match.enrollmentRequestedCnt) : 0;
            passRenderedData[index]       = match ? Number(match.passRenderedCnt)       : 0;
            registeredData[index]         = match ? Number(match.registeredCnt)         : 0;
          });

          this.chartData = {
            labels,
            datasets: [
              { label: 'Reenrollment Attempts', backgroundColor: '#C9512B', borderColor: '#C9512B', data: reenrollmentAttemptsData },
              { label: 'Pass Rendered',       backgroundColor: '#2AFFA2', borderColor: '#2AFFA2', data: passRenderedData },
              { label: 'Pass Registered',     backgroundColor: '#2726dd', borderColor: '#2726dd', data: registeredData },
            ]
          };

          this.hasData = true;
          this.refreshChart();
        },
        error: (error: unknown) => {
          this.isSpinnerHidden = true;
          this.customLoggerService.logError('Error fetching apple re-enrollments summary', error);
        }
      });
  }

  override initDatasource(): void {
    this.filterName$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.currentFilterName = value ?? '';
        this.pagination.clear();
        this.datasource?.adapter.reload(0);
      });

    this.datasource = new DatasourceCtor<IAppleReenrollmentDetails>({
      get: (index: number, count: number, success: (items: IAppleReenrollmentDetails[]) => void) => {
        this.customLoggerService.logDebug(`Requesting block: index=${index}, count=${count}, sort=${this.sortField} dir=${this.sortDirection}`);
        if (index < 0) { success([]); return; }

        const dir        = this.sortDirection;
        const blockSize  = count;
        const blockStart = Math.floor(index / blockSize) * blockSize;
        const startId    = this.pagination.resolveStartId(blockStart, dir, blockSize);
        this.customLoggerService.logDebug(`Resolved startId=${startId} for blockStart=${blockStart}, dir=${dir}`);

        if (startId == null) { success([]); return; }

        this.appleReenrollmentsService
          .getAppleReenrollmentsDetails(
            this.selectedBrandCode,
            this.selectedDateFrom, this.selectedDateTo,
            startId, blockSize, dir, this.sortField)
          .pipe(
            take(1),
            catchError((err: unknown) => {
              console.error('chunk error:', err);
              const empty: IAppleReenrollmentsDetailsChunkResponse = {
                summaryStatistics: { totalCnt: 0, minId: 0, maxId: 0 },
                appleReenrollmentEvents: []
              };
              return of(empty);
            })
          )
          .subscribe((resp: IAppleReenrollmentsDetailsChunkResponse) => {
            this.pagination.updateMeta(
              resp.summaryStatistics.totalCnt,
              resp.summaryStatistics.minId,
              resp.summaryStatistics.maxId
            );

            const rows: IAppleReenrollmentDetails[] = (resp.appleReenrollmentEvents ?? []).map(r => ({
              eventId:             r.eventId,
              brandCode:             r.brandCode,
              brandName:             r.brandName,
              accountNo:             r.accountNo,
              serialNo:              r.serialNo,
              appleDeviceId:         r.appleDeviceId,
              enrollmentRequestDt: r.enrollmentRequestDt,
              passRenderedDt:        r.passRenderedDt,
              passRegisteredDt:      r.passRegisteredDt
            }));

            if (rows.length > 0) {
              const lastId = rows.at(-1)?.eventId ?? 0;
              this.pagination.cacheNextBlock(dir, blockStart, blockSize, lastId);
            }
            success(rows);
          });
      },
      settings: {
        startIndex: 0,
        minIndex:   0,
        bufferSize: this.pagination.bufferSize,
        padding:    this.pagination.padding
      }
    });
  }
}
